import React, { useEffect } from 'react';
import { User, UserGroup } from '@/types';
import { Edit2, MapPin, PenTool, Sparkles, ShieldCheck, Trash2 } from 'lucide-react';
import { Pagination } from '@shared/components/common/Pagination';
import { useUsers, type UserListParams } from '@features/admin/data';

interface StaffListProps {
  page: number;
  pageSize: number;
  search: string;
  userGroups: UserGroup[];
  currentUserGroup: UserGroup | undefined;
  canEdit: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
  onViewSignature: (signature: string) => void;
  onItemsLoaded: (items: User[]) => void;
}

export const StaffList: React.FC<StaffListProps> = ({
  page,
  pageSize,
  search,
  userGroups,
  currentUserGroup,
  canEdit,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onDelete,
  onViewSignature,
  onItemsLoaded
}) => {
  const params: UserListParams = { page, pageSize, q: search || undefined };
  const result = useUsers(params);
  const users = result.items || [];
  const totalCount = Number(result.totalCount || 0);

  useEffect(() => {
    onItemsLoaded(users);
  }, [users, onItemsLoaded]);

  return (
    <>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <table className="w-full text-left">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="p-4 font-bold text-[10px] uppercase tracking-widest text-slate-500">
                Source
              </th>
              <th className="p-4 font-bold text-[10px] uppercase tracking-widest text-slate-500">
                Name & Email
              </th>
              <th className="p-4 font-bold text-[10px] uppercase tracking-widest text-slate-500">
                Access Group
              </th>
              <th className="p-4 font-bold text-[10px] uppercase tracking-widest text-slate-500">
                Authorized Sites
              </th>
              <th className="p-4 font-bold text-[10px] uppercase tracking-widest text-slate-500 text-center">
                Digital Sig
              </th>
              <th className="p-4 font-bold text-[10px] uppercase tracking-widest text-slate-500 text-right">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-8 text-center text-slate-500 italic">
                  No staff users found.
                </td>
              </tr>
            ) : (
              users.map((user) => {
                const group = userGroups.find((g) => g.id === user.userGroupId);
                const isGlobalAdminUser = group?.type === 'global_admin';
                const isUuid =
                  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(user.id);
                const canEditThisUser =
                  canEdit && (currentUserGroup?.type === 'global_admin' || !isGlobalAdminUser);
                const assignedCount = user.assignedVenueIds?.length || 0;

                return (
                  <tr
                    key={user.id}
                    className="last:border-0 hover:bg-slate-50/50 transition-colors group"
                  >
                    <td className="p-4">
                      {isUuid ? (
                        <div className="flex items-center gap-1.5 text-[9px] font-black uppercase text-teal-600 bg-teal-50 px-2 py-1 rounded-full border border-teal-100 w-fit">
                          <ShieldCheck className="w-3.5 h-3.5" /> Live Auth
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-[9px] font-black uppercase text-amber-600 bg-amber-50 px-2 py-1 rounded-full border border-amber-100 w-fit">
                          <Sparkles className="w-3.5 h-3.5" /> Demo/Seed
                        </div>
                      )}
                      <div className="mt-1.5 text-[9px] font-mono text-slate-300 px-2">
                        ID: {user.id.substring(0, 8)}...
                      </div>
                    </td>
                    <td className="p-4 font-medium text-slate-900">
                      <div className="font-bold flex items-center gap-2">
                        {user.name || (
                          <span className="text-slate-300 italic font-normal text-xs">
                            No Name Set
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-400 font-mono mt-0.5">
                        {user.email}
                      </div>
                    </td>
                    <td className="p-4">
                      <span
                        className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-tight border ${isGlobalAdminUser ? 'bg-purple-50 text-purple-700 border-purple-100' : 'bg-slate-100 text-slate-600 border-slate-200'}`}
                      >
                        {group?.name || 'Authorized Staff'}
                      </span>
                    </td>
                    <td className="p-4">
                      {assignedCount === 0 ? (
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">
                          Full Access
                        </span>
                      ) : (
                        <div className="flex items-center gap-1.5 text-teal-700 font-bold text-[10px] uppercase">
                          <MapPin className="w-3 h-3" /> {assignedCount} Sites
                        </div>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      {user.signature ? (
                        <button
                          onClick={() => onViewSignature(user.signature!)}
                          className="inline-flex items-center gap-1.5 text-teal-700 hover:text-teal-900 text-xs font-bold bg-teal-50 hover:bg-teal-100 px-3 py-1.5 rounded-lg border border-teal-100 transition-all shadow-sm"
                        >
                          <PenTool className="w-3 h-3" /> View
                        </button>
                      ) : (
                        <span className="text-[10px] text-slate-300 font-bold uppercase opacity-50">
                          Not Set
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {canEditThisUser ? (
                          <>
                            <button
                              onClick={() => onEdit(user)}
                              className="text-slate-400 hover:text-teal-600 p-2 hover:bg-white rounded-lg shadow-sm border border-transparent hover:border-slate-100"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => onDelete(user)}
                              className="text-slate-400 hover:text-red-600 p-2 hover:bg-white rounded-lg shadow-sm border border-transparent hover:border-slate-100"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <span className="text-[9px] text-slate-400 font-black uppercase tracking-widest px-2 py-2">
                            Protected
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <Pagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </>
  );
};
